/** DEBACQ / CWILL presentation adapter.
 * Native app layout, shipment state, form values and listeners stay intact.
 * One mutation observer. Native results, links and controls stay in place.
 * Dated history entries remain native nodes, presented as simple separated rows.
 * Horizontal progress widths stay app-controlled. No scroll loop or added requests.
 * One width-only ResizeObserver handles the app's native responsive sidebar.
 */
(() => {
  'use strict';
  if (!/\/tools\/tracking\/?$/.test(location.pathname)) return;
  const loader = document.getElementById('debacq-tracking-assets');
  if (!loader || loader.dataset.initialized === 'true') return;
  loader.dataset.initialized = 'true';
  const provider = /(?:^|\.)(?:seewill|cwill|parcelpanel|parcelwill|channelwill)\.com$/i;
  const credit = /^(?:powered\s+by\s*)?(?:see[\s-]*will|c[\s-]*will|parcel[\s-]*(?:panel|will)|channel[\s-]*will)(?:\s+(?:tracking|order tracking))?\s*[.®©™]*$/i;
  const states = /^status\s*:?\s*(?:in transit|out for delivery|delivered|pending|expired|exception|info received|information received|available for pickup|ready for pickup|not found|failed attempt|delivery failed)$/i;
  let main, root, observer, widthObserver, frame = 0, layoutFrame = 0;
  let observedWidths = new WeakMap();
  const metaColumns = new Map();
  const textOf = el => el.textContent.replace(/\s+/g, ' ').trim();

  function isCredit(el) {
    if (el === root || el.contains(root) || el.childElementCount > 5 ||
        el.querySelector('form,input,button,select,textarea,video,iframe,[role="button"]')) return false;
    const text = textOf(el);
    if (text.length > 90) return false;
    const links = [...el.querySelectorAll('a[href]'), ...(el.matches('a[href]') ? [el] : [])];
    const providerLink = links.some(a => {
      try { return provider.test(new URL(a.getAttribute('href'), document.baseURI).hostname); }
      catch (_) { return false; }
    });
    const logo = el.querySelector('img,svg');
    return credit.test(text) ||
      (/^powered\s+by\s*[:®©™]*$/i.test(text) && Boolean(logo || providerLink)) ||
      (!text && providerLink && Boolean(logo));
  }

  function hideCredit() {
    const scopes = [root, ...Array.from(root.parentElement?.children || [])
      .filter(el => el !== root && !el.matches('script,style,template'))];
    const candidates = new Set();
    scopes.forEach(scope => {
      if (scope !== root) candidates.add(scope);
      scope.querySelectorAll('a[href],p,span,div,footer').forEach(el => candidates.add(el));
    });
    // Reused app wrappers must become visible again if they acquire real content.
    candidates.forEach(el => {
      if (el.hasAttribute('data-dbq-tracking-credit') && !isCredit(el)) el.removeAttribute('data-dbq-tracking-credit');
    });
    candidates.forEach(el => {
      if (!isCredit(el)) return;
      let target = el;
      while (target.parentElement && target.parentElement !== root &&
          target.parentElement !== root.parentElement && isCredit(target.parentElement)) target = target.parentElement;
      target.setAttribute('data-dbq-tracking-credit', '');
    });
  }

  const fieldText = /^(?:order(?:\s+(?:number|id))?|e-?mail(?:\s+(?:address|or (?:phone|mobile)(?: number)?))?|phone(?: number)?|tracking(?: number)?)\s*[:*]?$/i;
  const fieldInputs = 'input:not([type="hidden"],[type="radio"],[type="checkbox"],[type="submit"],[type="button"]),textarea,select';

  function brandControls() {
    const controlMarkers = ['field', 'field-label', 'lookup', 'lookup-stack', 'lookup-shell', 'action-shell', 'actions', 'input-shell', 'unframed'];
    root.querySelectorAll(controlMarkers.map(key => '[data-dbq-tracking-' + key + ']').join(','))
      .forEach(el => controlMarkers.forEach(key => el.removeAttribute('data-dbq-tracking-' + key)));
    root.querySelectorAll('.pp_tracking_content .PP-TextField__Input, .pp_tracking_form input:not([type="hidden"],[type="radio"],[type="checkbox"],[type="submit"],[type="button"])')
      .forEach(input => {
        input.classList.add('input');
        let label = input.labels?.[0];
        if (label && !root.contains(label)) label = null;
        let field = input.parentElement;
        while (field && field !== root && !field.matches('form,.pp_tracking_form') &&
               field.querySelectorAll(fieldInputs).length === 1) {
          if (!label) {
            // CWILL also renders visible labels as ordinary divs without a for/id pair.
            label = [...field.querySelectorAll('label,div,span,p')].find(el =>
              !el.contains(input) && !el.querySelector(fieldInputs + ',button') &&
              fieldText.test(textOf(el)));
          }
          if (label && field.contains(label)) break;
          field = field.parentElement;
        }
        if (label) label.setAttribute('data-dbq-tracking-field-label', '');
        if (label && field && field !== root && !field.matches('form,.pp_tracking_form') &&
            field.contains(label) && field.querySelectorAll(fieldInputs).length === 1 && !field.querySelector('button')) {
          field.setAttribute('data-dbq-tracking-field', '');
          for (let wrapper = input.parentElement; wrapper && wrapper !== field; wrapper = wrapper.parentElement) {
            wrapper.setAttribute('data-dbq-tracking-input-shell', '');
          }
        }
      });
    root.querySelectorAll('.pp_tracking_content textarea').forEach(el => el.classList.add('textarea'));
    root.querySelectorAll('.pp_tracking_content button, .pp_tracking_form button').forEach(el => {
      if (el.matches('[role="tab"],[aria-controls],[aria-haspopup],[aria-expanded],[type="reset"],[class*="copy" i]')) return;
      const text = textOf(el);
      if (!text || (el.type !== 'submit' && !/^(?:track(?: (?:order|package|shipment|now))?|search|lookup|find(?: my)? order)$/i.test(text))) return;
      el.classList.add('button');
      el.setAttribute('data-dbq-tracking-button', '');
    });
    // Only a lookup form, never a results wrapper or an app's hidden alternate tab.
    const forms = [...root.querySelectorAll('form,.pp_tracking_form,.pp_tracking_content')].filter(el =>
      el.querySelector('[data-dbq-tracking-button]') && el.querySelector(fieldInputs) &&
      !el.querySelector('.pp_tracking_result,.pp_tracking_result_parent,.pp_timeline,.progress-bar-style'));
    forms.filter(el => !forms.some(inner => inner !== el && el.contains(inner))).forEach(form => {
      if (form.closest('[hidden],[aria-hidden="true"]') || getComputedStyle(form).display === 'none') return;
      form.setAttribute('data-dbq-tracking-lookup', '');
      const framed = Boolean(form.closest('.pp_tracking_form_div'));
      form.toggleAttribute('data-dbq-tracking-unframed', !framed);
      // The stock Contact form stretches its submit control to the fieldset.
      // Mark wrappers rather than moving React/app-owned inputs or buttons.
      form.querySelectorAll('[data-dbq-tracking-button]').forEach(button => {
        let action = button;
        for (let parent = button.parentElement; parent && parent !== form; parent = parent.parentElement) {
          if (parent.querySelector(fieldInputs + ',[role="tab"],.progress-bar-style')) break;
          parent.setAttribute('data-dbq-tracking-action-shell', '');
          action = parent;
        }
        if (action.parentElement === form) action.setAttribute('data-dbq-tracking-actions', '');
      });
      for (let parent = form.parentElement; framed && parent && parent !== root; parent = parent.parentElement) {
        if (parent.matches('.pp_tracking_form_div')) break;
        if (parent.querySelector('.pp_tracking_result,.pp_tracking_result_parent,.pp_timeline,.progress-bar-style')) break;
        parent.setAttribute('data-dbq-tracking-lookup-shell', '');
      }
      form.querySelectorAll('[data-dbq-tracking-field]').forEach(field => {
        for (let wrapper = field.parentElement; wrapper && wrapper !== form; wrapper = wrapper.parentElement) {
          if (wrapper.querySelectorAll('[data-dbq-tracking-field]').length > 1 &&
              !wrapper.querySelector('[role="tab"],.pp_timeline,.progress-bar-style')) {
            wrapper.setAttribute('data-dbq-tracking-lookup-stack', '');
          }
        }
      });
    });
  }

  function headings() {
    root.querySelectorAll('[data-dbq-tracking-heading-shell],[data-dbq-tracking-order-block]')
      .forEach(el => { el.removeAttribute('data-dbq-tracking-heading-shell'); el.removeAttribute('data-dbq-tracking-order-block'); });
    const candidates = [...root.querySelectorAll('h1,h2,h3,h4,h5,h6,p,div,span')]
      .filter(el => !el.closest('form,.pp_tracking_form,.progress-bar-style') &&
        !el.querySelector('form,button,input,ul,ol,.pp_timeline') && textOf(el).length < 110);
    const markHeading = (attribute, pattern) => {
      const matches = candidates.filter(el => pattern.test(textOf(el)));
      const leaves = matches.filter(el => !matches.some(child => child !== el && el.contains(child)));
      const targets = new Set(leaves.map(el => {
        const heading = el.closest('h1,h2,h3,h4,h5,h6');
        return heading && matches.includes(heading) ? heading : el;
      }));
      root.querySelectorAll('[' + attribute + ']').forEach(el => { if (!targets.has(el)) el.removeAttribute(attribute); });
      targets.forEach(el => {
        el.setAttribute(attribute, '');
        let block = el;
        for (let parent = el.parentElement; parent && parent !== root; parent = parent.parentElement) {
          // Empty-heading cleanup must never erase a form/result section boundary.
          if (parent.matches('.pp_tracking_form_div,.pp_tracking_content,.pp_tracking_result,.pp_tracking_result_parent') ||
              textOf(parent) !== textOf(el) ||
              parent.querySelector('form,button,input,ul,ol,img,svg,.pp_timeline,.progress-bar-style')) break;
          parent.setAttribute('data-dbq-tracking-heading-shell', '');
          block = parent;
        }
        if (attribute === 'data-dbq-tracking-order') block.setAttribute('data-dbq-tracking-order-block', '');
      });
    };
    markHeading('data-dbq-tracking-status', states);
    markHeading('data-dbq-tracking-order', /^order(?:\s+(?:number|no\.?))?\s*#\s*[\p{L}\p{N}_./-]+$/iu);
  }

  const painted = new WeakSet();
  const interactive = 'a,button,input,select,textarea,[role="button"],[role="link"],[tabindex],[contenteditable]';
  const guarded = interactive + ',[role="alert"],[class*="error" i],[class*="exception" i],[class*="failed" i]';
  const historyAttributes = ['history-group', 'event', 'event-shell', 'event-last', 'history-date', 'decoration'];
  const datePattern = /^(?:[A-Za-z]{3,9}\s+\d{1,2},?\s+\d{4}|\d{4}[-/]\d{1,2}[-/]\d{1,2}|\d{1,2}[./-]\d{1,2}[./-]\d{4})(?:\s|$)/;
  const mark = (el, key) => el.setAttribute('data-dbq-tracking-' + key, '');
  function leaves(scope, selector, pattern) {
    const matches = [...scope.querySelectorAll(selector)].filter(el => !el.closest(interactive) && pattern.test(textOf(el)));
    return matches.filter(el => !matches.some(child => child !== el && el.contains(child)));
  }

  function refineProgress() {
    root.querySelectorAll('.progress-bar-style').forEach(scope => {
      scope.querySelectorAll('[data-dbq-tracking-step-label],[data-dbq-tracking-step-date]').forEach(el => {
        el.removeAttribute('data-dbq-tracking-step-label'); el.removeAttribute('data-dbq-tracking-step-date');
      });
      leaves(scope, 'div,span,p,strong,b', /^(?:ordered|order ready|ready to ship|in transit|out for delivery|delivered|confirmed|shipped|pending|picked up)$/i)
        .forEach(el => mark(el, 'step-label'));
      leaves(scope, 'div,span,p,time', /^(?:[A-Za-z]{3,9}\s+\d{1,2}(?:,?\s+\d{4})?|\d{1,2}[./-]\d{1,2}(?:[./-]\d{2,4})?)$/)
        .forEach(el => mark(el, 'step-date'));
      [scope, ...scope.querySelectorAll('div,span,i,b')].forEach(el => {
        const unsafe = el.closest(guarded) || el.querySelector(interactive);
        [...el.attributes].forEach(attr => {
          if (/^data-dbq-tracking-rail/.test(attr.name) &&
              (unsafe || (!/(?:before|after)$/.test(attr.name) && textOf(el)))) {
            el.removeAttribute(attr.name); painted.delete(el);
          }
        });
        if (unsafe || painted.has(el)) return;
        let available = false;
        ['', '::before', '::after'].forEach(pseudo => {
          if (!pseudo && (textOf(el) || el.querySelector('img,svg,video,iframe,canvas,[class*="node"],[class*="label"]'))) return;
          const cs = getComputedStyle(el, pseudo || null);
          if (cs.display === 'none' || (pseudo && ['none', 'normal'].includes(cs.content))) return;
          const w = parseFloat(cs.width), h = parseFloat(cs.height);
          if (!(w > 0 && h > 0)) return;
          available = true;
          const solid = cs.backgroundImage !== 'none' || !['rgba(0, 0, 0, 0)', 'transparent'].includes(cs.backgroundColor);
          if (w >= 40 && h <= 16 && w / h >= 6 && solid) mark(el, 'rail' + (pseudo ? '-' + pseudo.slice(2) : ''));
        });
        if (available) painted.add(el);
      });
    });
  }

  function refineHistory() {
    const scopes = [...root.querySelectorAll('.pp_timeline')].filter(el => !el.parentElement?.closest('.pp_timeline'));
    const groups = new Map();
    scopes.forEach(scope => {
      [scope, ...scope.querySelectorAll('*')].forEach(el => {
        historyAttributes.forEach(key => el.removeAttribute('data-dbq-tracking-' + key));
      });
      const dates = leaves(scope, 'time,div,span,p,dt', datePattern).filter(el => textOf(el).length < 100);
      if (!dates.length) return;
      const count = new Map();
      dates.forEach(date => {
        for (let el = date; el && scope.contains(el); el = el.parentElement) count.set(el, (count.get(el) || 0) + 1);
      });
      const entries = [];
      dates.forEach(date => {
        const candidates = [];
        for (let el = date.parentElement; el && scope.contains(el) && count.get(el) === 1; el = el.parentElement) {
          if (el.matches(guarded) || el.querySelector('form,input,select,textarea')) break;
          if (el !== scope && textOf(el).length > textOf(date).length) candidates.push(el);
        }
        // Prefer the OUTER list item, not an inner class named "item-content".
        // Otherwise every row becomes its own one-entry group and loses its gap.
        const event = [...candidates].reverse().find(el => el.matches('li,[role="listitem"]')) ||
          candidates[candidates.length - 1] || (dates.length === 1 && textOf(scope).length > textOf(date).length ? scope : null);
        if (event && !entries.some(entry => entry.event.contains(event) || event.contains(entry.event))) entries.push({event, date});
      });
      // Leave unfamiliar layouts alone rather than partially deleting decorations.
      if (entries.length !== dates.length) return;
      mark(scope, 'history-group');
      entries.forEach(({event, date}) => {
        mark(event, 'event'); mark(date, 'history-date');
        for (let parent = event.parentElement; parent && scope.contains(parent); parent = parent.parentElement) mark(parent, 'history-group');
        event.querySelectorAll('div,span,p,time,dt,dd,i,b,strong,svg').forEach(el => {
          if (el === date || el.closest(guarded) || el.querySelector('button,input,select,textarea,[role="button"],img,video,iframe,canvas')) return;
          if (textOf(el)) mark(el, 'event-shell');
          else mark(el, 'decoration');
        });
      });
      // Multiple timeline roots can each hold one shipment event. Group direct
      // sibling roots before deciding which row is last, so only one loses its gap.
      const siblings = scopes.filter(other => other.parentElement === scope.parentElement);
      const key = siblings.length > 1 ? scope.parentElement : scope;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(...entries.map(entry => entry.event));
      scope.querySelectorAll('div,span,i,svg').forEach(el => {
        if (!el.hasAttribute('data-dbq-tracking-event') && !el.closest('[data-dbq-tracking-event]') &&
            !el.closest(guarded) && !el.querySelector(interactive + ',img,video,iframe,canvas') && !textOf(el)) mark(el, 'decoration');
      });
    });
    groups.forEach(events => { if (events.length) mark(events[events.length - 1], 'event-last'); });
  }

  const tailShells = new Set();
  function resultLayout() {
    tailShells.forEach(el => el.removeAttribute('data-dbq-tracking-tail-shell'));
    tailShells.clear();
    const ready = Boolean(root.querySelector('.pp_tracking_form input,.PP-TextField__Input,[data-dbq-tracking-order],.pp_timeline'));
    root.toggleAttribute('data-dbq-tracking-ready', ready);
    root.querySelectorAll('.pp_tracking_result,.pp_tracking_result_parent').forEach(el => {
      el.toggleAttribute('data-dbq-tracking-results-shell', Boolean(el.querySelector('.pp_timeline,.progress-bar-style,[data-dbq-tracking-order],[data-dbq-tracking-status]')));
    });
    if (!ready) return;
    const addShell = el => { mark(el, 'tail-shell'); tailShells.add(el); };
    // Remove end padding/min-heights on layout wrappers around real results,
    // not from individual events or product cards. One inset stays on root.
    root.querySelectorAll('[data-dbq-tracking-history-group],[data-dbq-tracking-meta-section],.pp_tracking_form_div,[data-dbq-tracking-lookup]').forEach(content => {
      for (let shell = content.parentElement; shell && shell !== root; shell = shell.parentElement) {
        if (shell.matches('form')) continue;
        if (!shell.matches('div,section,article,aside') || shell.matches('[role="tablist"]')) break;
        addShell(shell);
      }
    });
    // App-proxy output can have an additional wrapper outside its named root.
    // Reset it ONLY if the wrapper has no content except this app (and assets).
    let child = root;
    for (let shell = root.parentElement; shell && shell !== main; shell = shell.parentElement) {
      if (!shell.matches('div,section') || shell.matches('header,footer,[role="banner"],[role="contentinfo"]')) break;
      const otherContent = [...shell.childNodes].some(node => {
        if (node.nodeType === Node.TEXT_NODE) return Boolean(node.textContent.trim());
        return node.nodeType === Node.ELEMENT_NODE && node !== child &&
          !node.matches('script,style,template,noscript,[data-dbq-tracking-credit]');
      });
      if (otherContent) break;
      addShell(shell);
      child = shell;
    }
  }

  function metaLabels() {
    root.querySelectorAll('[data-dbq-tracking-meta-label],[data-dbq-tracking-meta-section]').forEach(el => {
      el.removeAttribute('data-dbq-tracking-meta-label'); el.removeAttribute('data-dbq-tracking-meta-section'); el.removeAttribute('data-dbq-tracking-meta-kind');
    });
    const labels = leaves(root, 'div,span,p,h3,h4,h5,h6', /^(?:carrier|products?|tracking number)\s*:?$/i)
      .filter(el => !el.closest('.pp_tracking_form_div,.pp_tracking_form,.progress-bar-style,.pp_timeline'));
    labels.forEach(label => {
      mark(label, 'meta-label');
      let block = label.parentElement;
      while (block && block !== root && textOf(block) === textOf(label)) block = block.parentElement;
      if (block && block !== root && !block.querySelector('form,.pp_timeline,.progress-bar-style') &&
          labels.filter(el => block.contains(el)).length === 1) {
        mark(block, 'meta-section');
        block.setAttribute('data-dbq-tracking-meta-kind', /^products?$/i.test(textOf(label)) ? 'product' : 'carrier');
      }
    });
  }

  function metadataLayout() {
    root.querySelectorAll('[data-dbq-tracking-meta-last]').forEach(el => {
      el.removeAttribute('data-dbq-tracking-meta-last');
    });
    const next = new Map();
    root.querySelectorAll('[data-dbq-tracking-meta-section]').forEach(section => {
      let column = section;
      for (let parent = section.parentElement; parent && parent !== root; parent = parent.parentElement) {
        const history = parent.querySelector('.pp_timeline');
        if (history && !column.contains(history) &&
            !column.querySelector('form,.pp_tracking_form,.progress-bar-style')) {
          const historyGroups = [...parent.querySelectorAll('.pp_timeline')].filter(el => !column.contains(el));
          if (!next.has(column)) next.set(column, { history: historyGroups, sections: [] });
          next.get(column).sections.push(section);
          break;
        }
        column = parent;
      }
    });
    metaColumns.forEach((_, column) => {
      if (!next.has(column)) {
        widthObserver?.unobserve(column);
        column.removeAttribute('data-dbq-tracking-meta-column');
        column.removeAttribute('data-dbq-tracking-meta-stacked');
      }
    });
    const previousColumns = new Set(metaColumns.keys());
    metaColumns.clear();
    next.forEach((value, column) => {
      mark(column, 'meta-column');
      mark(value.sections[value.sections.length - 1], 'meta-last');
      metaColumns.set(column, value);
      if (!previousColumns.has(column)) widthObserver?.observe(column);
    });
    // Standalone metadata still needs a definitive last item even if the app
    // has no timeline (pending / not found / partial results).
    const groups = new Map();
    root.querySelectorAll('[data-dbq-tracking-meta-section]').forEach(el => {
      if (el.closest('[data-dbq-tracking-meta-column]')) return;
      const list = groups.get(el.parentElement) || [];
      list.push(el); groups.set(el.parentElement, list);
    });
    groups.forEach(list => mark(list[list.length - 1], 'meta-last'));
    measureMetadata();
  }

  function measureMetadata() {
    layoutFrame = 0;
    if (!root?.isConnected) return;
    metaColumns.forEach(({history}, column) => {
      if (!column.isConnected || !column.getClientRects().length) return;
      const col = column.getBoundingClientRect();
      const boxes = history.filter(el => el.isConnected && el.getClientRects().length)
        .map(el => el.getBoundingClientRect());
      if (!boxes.length) return;
      const end = Math.max(...boxes.map(rect => rect.bottom));
      const overlapsInline = boxes.some(rect => col.left < rect.right - 1 && col.right > rect.left + 1);
      const stacked = overlapsInline && col.top >= end - 1;
      // Sibling carrier/product sections do not each need a fresh mobile gap.
      const previousMeta = column.previousElementSibling?.hasAttribute('data-dbq-tracking-meta-section');
      column.toggleAttribute('data-dbq-tracking-meta-stacked', stacked && !previousMeta);
    });
  }

  function observeWidth() {
    widthObserver?.disconnect();
    observedWidths = new WeakMap();
    if (!root || !window.ResizeObserver) return;
    widthObserver = new ResizeObserver(entries => {
      let changed = false;
      entries.forEach(entry => {
        const width = entry.contentRect.width;
        const previous = observedWidths.get(entry.target);
        if (previous === undefined || Math.abs(width - previous) >= 0.5) {
          observedWidths.set(entry.target, width);
          changed = true;
        }
      });
      // Height changes from the gap itself do not schedule another layout pass.
      if (changed && !layoutFrame) layoutFrame = requestAnimationFrame(measureMetadata);
    });
    widthObserver.observe(root);
  }

  function productQuantity() {
    // Mark only to exempt the app's badge from normal paragraph typography.
    // No new badge, moved node, visible caption or replacement positioning.
    root.querySelectorAll('[data-dbq-tracking-quantity]').forEach(el => {
      if (!/^\d{1,4}$/.test(textOf(el)) || !el.closest('.pp_tracking_product_show,[data-dbq-tracking-meta-kind="product"]'))
        el.removeAttribute('data-dbq-tracking-quantity');
    });
    root.querySelectorAll('.pp_tracking_product_show,[data-dbq-tracking-meta-kind="product"]').forEach(scope => {
      scope.querySelectorAll('span,small,sup,b,em,i,div').forEach(el => {
        if (!/^\d{1,4}$/.test(textOf(el)) || el.childElementCount || el.closest(interactive)) return;
        const named = /(?:quantity|qty|count|(?:^|[_-])num(?:ber)?(?:[_-]|$))/i.test(el.className);
        const overlay = el.parentElement?.querySelector('img') &&
          (getComputedStyle(el).position === 'absolute' || el.tagName === 'SUP');
        if (named || overlay) mark(el, 'quantity');
      });
    });
  }

  function carrierLabel() {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (node.parentElement?.closest('script,style,textarea,input,[contenteditable]')) continue;
      const match = node.data.match(/^(\s*)last[\s\u2010-\u2015-]+mile[\s\u2010-\u2015-]+carrier(\s*[:\uFF1A]?\s*)$/i);
      if (match) node.data = match[1] + (loader.dataset.carrierLabel || 'Carrier') + match[2];
    }
  }

  function sampleButton() {
    const template = document.getElementById('debacq-tracking-native-controls');
    if (!template?.content) return;
    const sample = document.createElement('div');
    sample.setAttribute('aria-hidden', 'true');
    sample.inert = true;
    sample.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;contain:layout style';
    sample.append(template.content.cloneNode(true));
    document.body.append(sample);
    const button = sample.querySelector('.button');
    if (button) {
      const style = getComputedStyle(button);
      root.style.setProperty('--dbq-tracking-button-padding', style.padding);
      root.style.setProperty('--dbq-tracking-button-transition', style.transition);
    }
    sample.remove();
  }

  function refresh() {
    frame = 0;
    observer?.disconnect();
    try {
      const next = root?.isConnected ? root : main.querySelector('#pp-tracking-page-app');
      if (next && next !== root) { root = next; sampleButton(); observeWidth(); }
      if (next) { brandControls(); headings(); refineProgress(); refineHistory(); carrierLabel(); metaLabels(); productQuantity(); hideCredit(); resultLayout(); metadataLayout(); }
      else root = null;
    } finally {
      if (observer && main?.isConnected) observer.observe(main, {childList:true, subtree:true, characterData:true, attributes:true, attributeFilter:['class','style','hidden','aria-hidden','aria-busy']});
    }
  }
  function schedule() { if (!frame) frame = requestAnimationFrame(refresh); }
  function start() {
    if (observer) return;
    main = document.getElementById('main');
    if (!main) return;
    observer = new MutationObserver(records => {
      if (!root?.isConnected || records.some(r => root.contains(r.target) || r.target === root.parentElement || tailShells.has(r.target))) schedule();
    });
    refresh();
  }
  function stop() {
    observer?.disconnect();
    widthObserver?.disconnect();
    if (layoutFrame) cancelAnimationFrame(layoutFrame);
    layoutFrame = 0;
    metaColumns.forEach((_, column) => {
      column.removeAttribute('data-dbq-tracking-meta-column');
      column.removeAttribute('data-dbq-tracking-meta-stacked');
    });
    metaColumns.clear();
    tailShells.forEach(el => el.removeAttribute('data-dbq-tracking-tail-shell'));
    tailShells.clear();
    if (frame) cancelAnimationFrame(frame);
    observer = root = null;
    frame = 0;
  }
  window.addEventListener('pagehide', stop);
  window.addEventListener('pageshow', event => { if (event.persisted) start(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once:true});
  else start();
})();
